import { useEffect, useMemo, useState, useCallback, memo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectCardData, selectChecklistData, selectQueuedCardsDisplay, selectParallelData, selectChecklistMetadata, selectChecklistCardIds, selectChecklistCache, selectViewingCardData } from '../../store/card/card.selector.js';
import { fetchChecklistCardIds, fetchChecklistDataByIds, fetchCollectionData, fetchParallelData } from '../../utils/firebase/firebase.utils.js';
import { fetchCollectionDataOnly } from '../../utils/firebase/collection.js';
import { fetchCustomCategoryCardIds } from '../../utils/firebase/checklist.js';
import { setChecklistData, setChecklistCardIds, setParallelData, setCardData, setChecklistCache, clearChecklistCacheBySeries, setChecklistMetadata, setViewingCardData, clearViewingCardData } from '../../store/card/card.action.js';
import { selectUser } from '../../store/user/user.selector.js';
import { fetchPublicProfile } from '../../utils/firebase/publicProfile.js';

// styles
import {
    SeriesDisplayContainer,
    CollectionStats,
    DisplayHeader,
    LeftFilterContainer,
    ChecklistControls,
    ControlContent,
    BodyContainer,
    LeftContainer,
    CardsDisplayContainer,
    CardsDisplay,
    InputField,
    QueuedCardsContainer,
    FixedActionButtonsContainer
} from './SeriesDisplay.styles.js';

// components
import ChecklistItemDisplay from '../checklist-item-display/ChecklistItemDisplay.js';
import FriendsChecklistDisplayItem from '../../pages/friends-page/components/friends-checklist-display-item/FriendsChecklistDisplayItem.js';
import LoadingIcon from '../loading-icon/LoadingIcon.js';
import SystemPaginationControls from '../system/pagination-controls/SystemPaginationControls.js';
import QueuedCards from '../queued-cards/QueuedCards.js';
import StatBox from '../stat-box/StatBox.js';
import PrimaryBtn from '../btns/primary-btn/PrimaryBtn.js';
import AddCustomCardModal from '../../pages/checklist-page/components/checklist-header/components/add-custom-card-modal/AddCustomCardModal.js';
import BatchSetSelectionModal from '../set-selection-modal/BatchSetSelectionModal.js';
import BatchListingSelectionModal from '../listing-selection-modal/BatchListingSelectionModal.js';

export default function SeriesDisplay({ viewingUserID = null }) {
    const dispatch = useDispatch();
    const { sport, year, manufacturer, series, cardType, cardTypeCategory } = useParams();
    const cardData = useSelector(selectCardData);
    const checklistData = useSelector(selectChecklistData);
    const user = useSelector(selectUser);
    const queuedCardsDisplay = useSelector(selectQueuedCardsDisplay);
    const parallelData = useSelector(selectParallelData);
    const checklistMetadata = useSelector(selectChecklistMetadata);
    const checklistCardIds = useSelector(selectChecklistCardIds);
    const checklistCache = useSelector(selectChecklistCache);
    const viewingCardData = useSelector(selectViewingCardData);
    
    // Use viewingUserID if provided, otherwise use current user's ID
    const targetUserID = viewingUserID || user?.uid;

    // Use viewingCardData when viewing a friend's collection, otherwise use regular cardData
    // If viewing a friend but viewingCardData doesn't match or doesn't exist, use empty object to prevent showing wrong data
    const effectiveCardData = useMemo(() => {
        if (viewingUserID) {
            const hasMatchingViewingData = viewingCardData?.friendUID === viewingUserID;
            return hasMatchingViewingData ? viewingCardData.cardData : {};
        }
        return cardData;
    }, [viewingUserID, viewingCardData, cardData]);

    const effectiveChecklistData = useMemo(() => {
        return viewingUserID
            ? (viewingCardData?.friendUID === viewingUserID ? viewingCardData.checklistData : {})
            : checklistData;
    }, [viewingUserID, viewingCardData, checklistData]);


    const [searchInputValue, setSearchInputValue] = useState(''); // Immediate value for input field
    const [enteredSearchTerm, setEnteredSearchTerm] = useState(''); // Debounced value for filtering/fetching
    // When viewing a friend's collection, default to showing collected cards (isToggled = false)
    // When viewing own collection, default to showing uncollected cards (isToggled = true)
    const [isToggled, setIsToggled] = useState(viewingUserID ? false : true); // Controls collected vs uncollected view
    const [selectedTeam, setSelectedTeam] = useState('All Teams');
    const [teams, setTeams] = useState([]);
    const [selectedStat, setSelectedStat] = useState("Completion");
    const [selectedParallel, setSelectedParallel] = useState('All Parallels');
    const [isFixed, setIsFixed] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    // Use a stable page size to prevent overlap/skips when data size changes
    const cardsPerPage = 40;
    const [isLoading, setIsLoading] = useState(true);
    const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);
    const [selectedCards, setSelectedCards] = useState(new Set()); // Set of card numbers
    const [showBatchSetModal, setShowBatchSetModal] = useState(false);
    const [showBatchListingModal, setShowBatchListingModal] = useState(false);

    useEffect(() => {
        let timeoutId;
        const handleScroll = () => {
            // Debounce scroll events to improve performance
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                const scrollThreshold = 475; // 427px
                if (window.scrollY >= scrollThreshold) {
                    setIsFixed(true);
                } else {
                    setIsFixed(false);
                }
            }, 10); // 10ms debounce
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(timeoutId);
        };
    }, []);

    // Debounce search input to reduce unnecessary filtering/fetching
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setEnteredSearchTerm(searchInputValue);
        }, 500); // 400ms delay - wait for user to stop typing

        return () => clearTimeout(timeoutId);
    }, [searchInputValue]);

    const teamFilteredStats = useMemo(() => {
        let totalTeamCards = 0;
        let collectedTeamCards = 0;
        let totalCollectedTeamCards = 0;
        let parallelCardsCount = 0;
        let numberedCardsCount = 0;
        let duplicatesCount = 0;
        let baseCardsCount = 0;

        // Total cards in checklist (already filtered by team via fetchChecklistCardIds)
        // checklistCardIds is already filtered by selectedTeam when fetched
        totalTeamCards = checklistCardIds ? checklistCardIds.length : 0;

        // If no checklist card IDs, return zeros
        if (!checklistCardIds || checklistCardIds.length === 0) {
            return {
                totalTeamCards: 0,
                collectedTeamCards: 0,
                totalCollectedTeamCards: 0,
                parallelCardsCount: 0,
                numberedCardsCount: 0,
                duplicatesCount: 0,
                uncollectedTeamCards: 0,
                baseCardsCount: 0
            };
        }

        // Create a Set for fast lookup of valid card IDs (already team-filtered)
        const validCardIdsSet = new Set(checklistCardIds);

        // Process all collected cards, but only count those that match the filtered checklist
        // This ensures we count all collected cards, not just loaded ones
        Object.keys(effectiveCardData).forEach(cardNumber => {
            // Only count cards that are in the filtered checklist (team-filtered)
            if (!validCardIdsSet.has(cardNumber)) {
                return;
            }

            const collectedCard = effectiveCardData[cardNumber];
            if (!collectedCard || !collectedCard.collectedCards) {
                return;
            }

            collectedTeamCards++;

            // OPTIMIZED: Use pre-computed totalQty if available, otherwise compute
            const totalQty = collectedCard.totalQty !== undefined 
                ? collectedCard.totalQty 
                : collectedCard.collectedCards.reduce((sum, p) => sum + (p.cardQty || 0), 0);
            totalCollectedTeamCards += totalQty;

            // OPTIMIZED: Use pre-computed flags if available, otherwise compute
            if (collectedCard.hasBase !== undefined) {
                // Use pre-computed flags
                if (collectedCard.hasBase) {
                    const baseParallel = collectedCard.collectedCards.find(p => p.cardParallelName === "BASE");
                    if (baseParallel) {
                        baseCardsCount += baseParallel.cardQty;
                    }
                }
                if (collectedCard.hasParallels) {
                    parallelCardsCount++;
                }
                if (collectedCard.hasNumbered) {
                    numberedCardsCount++;
                }
                if (collectedCard.hasDuplicates) {
                    duplicatesCount++;
                }
            } else {
                // Fallback: compute on-the-fly for backward compatibility
                collectedCard.collectedCards.forEach((parallel) => {
                    // Count duplicates (cards with qty > 1)
                    if (parallel.cardQty > 1) {
                        duplicatesCount++;
                    }

                    // Count total base cards (sum of quantities of all BASE parallels)
                    if (parallel.cardParallelName === "BASE") {
                        baseCardsCount += parallel.cardQty;
                    }
                });

                // Count parallel cards (cards with non-BASE parallels)
                if (collectedCard.collectedCards.some(card => card.cardParallelName !== "BASE")) {
                    parallelCardsCount++;
                }

                // Count numbered cards (cards with numbered parallels)
                if (collectedCard.collectedCards.some(card => card.cardPrintNumber)) {
                    numberedCardsCount++;
                }
            }
        });

        // Calculate uncollected cards
        const uncollectedTeamCards = totalTeamCards - collectedTeamCards;

        return {
            totalTeamCards,
            collectedTeamCards,
            totalCollectedTeamCards,
            parallelCardsCount,
            numberedCardsCount,
            duplicatesCount,
            uncollectedTeamCards,
            baseCardsCount
        };
    }, [effectiveCardData, checklistCardIds]);

    const completionPercentage = teamFilteredStats.totalTeamCards > 0
        ? Math.round((teamFilteredStats.collectedTeamCards / teamFilteredStats.totalTeamCards) * 100)
        : 0;

    // Memoize computed values to prevent unnecessary re-renders
    const isCustomCategory = useMemo(() => {
        return cardTypeCategory && cardTypeCategory.startsWith('custom:');
    }, [cardTypeCategory]);
    
    const customCategoryId = useMemo(() => {
        return isCustomCategory ? cardTypeCategory.replace('custom:', '') : null;
    }, [isCustomCategory, cardTypeCategory]);

    // Track if initial fetch has completed to prevent duplicate fetches
    const hasInitialFetchRef = useRef(false);
    const lastFetchParamsRef = useRef('');
    const lastSeriesRef = useRef('');

    // Helper function to create cache key
    const getCacheKey = useCallback((sport, year, manufacturer, series, cardType, cardTypeCategory) => {
        return `checklists/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}`;
    }, []);

    // Clear cache when navigating to different series
    useEffect(() => {
        const currentSeries = `${sport}/${year}/${manufacturer}/${series}`;
        if (lastSeriesRef.current && lastSeriesRef.current !== currentSeries) {
            // User navigated to a different series, clear cache for old series
            const [oldSport, oldYear, oldManufacturer, oldSeries] = lastSeriesRef.current.split('/');
            dispatch(clearChecklistCacheBySeries(oldSport, oldYear, oldManufacturer, oldSeries));
        }
        lastSeriesRef.current = currentSeries;
    }, [sport, year, manufacturer, series, dispatch]);

    // Clear cardData immediately when switching to friend's view to prevent showing wrong data
    useEffect(() => {
        if (viewingUserID) {
            // Immediately clear cardData when viewing a friend
            dispatch(setCardData({}));
        }
    }, [viewingUserID, dispatch]);

    // Clear viewingCardData when unmounting while viewing a friend (e.g. navigating to "my collection")
    // This ensures we clear even when the destination route doesn't mount SeriesDisplay (e.g. /checklists without cardType)
    useEffect(() => {
        if (!viewingUserID) return;
        return () => {
            dispatch(clearViewingCardData());
        };
    }, [viewingUserID, dispatch]);

    // Check for stale data and clear viewingCardData when switching friends (same session, different friend)
    useEffect(() => {
        if (viewingUserID) {
            if (viewingCardData?.friendUID && viewingCardData.friendUID !== viewingUserID) {
                dispatch(clearViewingCardData());
            }
        } else {
            if (viewingCardData) {
                dispatch(clearViewingCardData());
            }
        }
    }, [viewingUserID, viewingCardData, dispatch]);

    // Check for stale data when viewing a friend's collection
    useEffect(() => {
        const checkAndRefreshStaleData = async () => {
            if (!viewingUserID || !viewingCardData || viewingCardData.friendUID !== viewingUserID) {
                return;
            }

            try {
                const friendUserData = await fetchPublicProfile(viewingUserID);
                if (!friendUserData || !friendUserData.collectionLastUpdated) {
                    return; // No timestamp available, can't check
                }

                const friendLastUpdated = friendUserData.collectionLastUpdated;
                const cachedLastUpdated = viewingCardData.collectionLastUpdated;

                const friendTimestamp = friendLastUpdated?.toDate ? friendLastUpdated.toDate().getTime() : 
                                      friendLastUpdated?.seconds ? friendLastUpdated.seconds * 1000 : 
                                      new Date(friendLastUpdated).getTime();
                
                const cachedTimestamp = cachedLastUpdated?.toDate ? cachedLastUpdated.toDate().getTime() :
                                       cachedLastUpdated?.seconds ? cachedLastUpdated.seconds * 1000 :
                                       cachedLastUpdated || 0;

                if (friendTimestamp > cachedTimestamp) {
                    dispatch(clearViewingCardData());
                    hasInitialFetchRef.current = false;
                    lastFetchParamsRef.current = '';
                }
            } catch (error) {
                // Error checking stale data - ignore
            }
        };

        // Only check if we have all required params AND viewingCardData exists
        if (viewingUserID && viewingCardData && viewingCardData.friendUID === viewingUserID && sport && year && manufacturer && series && cardType && cardTypeCategory) {
            checkAndRefreshStaleData();
        }
    }, [viewingUserID, viewingCardData, sport, year, manufacturer, series, cardType, cardTypeCategory, dispatch]);

    // Fetch collection, parallel data, and all card IDs on mount or when route params change
    useEffect(() => {
        if (!targetUserID) {
            return;
        }
        
        const fetchKey = `${targetUserID}-${sport}-${year}-${manufacturer}-${series}-${cardType}-${cardTypeCategory}`;
        
        if (hasInitialFetchRef.current && lastFetchParamsRef.current === fetchKey) {
            return;
        }

        // Check cache first (only for official categories, not custom)
        const cacheKey = getCacheKey(sport, year, manufacturer, series, cardType, cardTypeCategory);
        const cachedData = checklistCache[cacheKey];

        if (cachedData && !isCustomCategory) {
            // Load metadata and parallel data from cache (these are the same for all teams)
            dispatch(setChecklistMetadata(cachedData.metadata || { sortOrder: 'numeric', teams: {} }));
            dispatch(setParallelData(cachedData.parallelData || []));
            
            // Update cache access time
            dispatch(setChecklistCache(cacheKey, cachedData));
            
            // Still need to fetch collection data (user-specific) and card IDs (team-filtered)
            // But we can use cached metadata for the correct sortOrder
            const collectionPath = `users/${targetUserID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}`;
            const checklistPath = `checklists/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}`;
            
            // When viewing a friend, fetch without dispatching to cardData
            if (viewingUserID) {
                Promise.all([
                    fetchCollectionDataOnly(collectionPath),
                    dispatch(fetchChecklistCardIds(checklistPath, {
                        team: selectedTeam,
                        sortOrder: cachedData.metadata?.sortOrder || 'numeric'
                    }))
                ]).then(async ([collectionResult]) => {
                    const friendUserData = await fetchPublicProfile(viewingUserID);
                    const collectionLastUpdated = friendUserData?.collectionLastUpdated || null;
                    
                    dispatch(setViewingCardData(
                        viewingUserID,
                        collectionResult || {},
                        {},
                        Date.now(),
                        collectionLastUpdated
                    ));
                    dispatch(setCardData({}));
                }).catch(() => {
                    // Error fetching friend collection
                }).finally(() => {
                    setIsLoading(false);
                });
            } else {
                Promise.all([
                    dispatch(fetchCollectionData(collectionPath)),
                    dispatch(fetchChecklistCardIds(checklistPath, {
                        team: selectedTeam,
                        sortOrder: cachedData.metadata?.sortOrder || 'numeric'
                    }))
                ]).finally(() => {
                    setIsLoading(false);
                });
            }
            
            hasInitialFetchRef.current = true;
            lastFetchParamsRef.current = fetchKey;
            return;
        }
        
        hasInitialFetchRef.current = true;
        lastFetchParamsRef.current = fetchKey;
        setIsLoading(true);
        
        // Clear cardData immediately when viewing a friend to prevent showing wrong collection
        if (viewingUserID) {
            dispatch(setCardData({}));
        }

        if (isCustomCategory && customCategoryId) {
            // For custom categories, use the full cardTypeCategory (with "custom:" prefix) for the Firestore path
            const collectionPath = `users/${targetUserID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}`;

            // When viewing a friend, fetch without dispatching to cardData
            if (viewingUserID) {
                Promise.all([
                    fetchCollectionDataOnly(collectionPath),
                    fetchCustomCategoryCardIds(targetUserID, sport, year, manufacturer, series, cardType, cardTypeCategory).then(cardIds => {
                        dispatch(setChecklistCardIds(cardIds));
                    }),
                    Promise.resolve(dispatch(setParallelData([])))
                ]).then(async ([collectionResult]) => {
                    // Fetch friend's collectionLastUpdated timestamp
                    const friendUserData = await fetchPublicProfile(viewingUserID);
                    const collectionLastUpdated = friendUserData?.collectionLastUpdated || null;
                    
                    dispatch(setViewingCardData(
                        viewingUserID,
                        collectionResult || {},
                        {},
                        Date.now(),
                        collectionLastUpdated
                    ));
                    // Ensure cardData is empty
                    dispatch(setCardData({}));
                }).finally(() => {
                    setIsLoading(false);
                });
            } else {
                Promise.all([
                    dispatch(fetchCollectionData(collectionPath)),
                    fetchCustomCategoryCardIds(targetUserID, sport, year, manufacturer, series, cardType, cardTypeCategory).then(cardIds => {
                        dispatch(setChecklistCardIds(cardIds));
                    }),
                    Promise.resolve(dispatch(setParallelData([])))
                ]).finally(() => {
                    setIsLoading(false);
                });
            }
        } else {
            // For official categories, use existing logic
            const collectionPath = `users/${targetUserID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}`;
            const checklistPath = `checklists/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}`;

            // When viewing a friend, fetch without dispatching to cardData
            if (viewingUserID) {
                Promise.all([
                    fetchCollectionDataOnly(collectionPath),
                    dispatch(fetchParallelData(sport, year, manufacturer, series, cardType, cardTypeCategory)),
                    dispatch(fetchChecklistCardIds(checklistPath, {
                        team: selectedTeam,
                        sortOrder: 'numeric' // Default, will be corrected when metadata loads
                    }))
                ]).then(async ([collectionResult]) => {
                    const friendUserData = await fetchPublicProfile(viewingUserID);
                    const collectionLastUpdated = friendUserData?.collectionLastUpdated || null;
                    
                    dispatch(setViewingCardData(
                        viewingUserID,
                        collectionResult || {},
                        {},
                        Date.now(),
                        collectionLastUpdated
                    ));
                    dispatch(setCardData({}));
                }).catch(() => {
                    // Error fetching friend collection
                }).finally(() => {
                    setIsLoading(false);
                });
            } else {
                Promise.all([
                    dispatch(fetchCollectionData(collectionPath)),
                    dispatch(fetchParallelData(sport, year, manufacturer, series, cardType, cardTypeCategory)),
                    dispatch(fetchChecklistCardIds(checklistPath, {
                        team: selectedTeam,
                        sortOrder: 'numeric' // Default, will be corrected when metadata loads
                    }))
                ]).finally(() => {
                    setIsLoading(false);
                });
            }
        }
    }, [targetUserID, dispatch, sport, year, manufacturer, series, cardType, cardTypeCategory, isCustomCategory, customCategoryId, viewingUserID, user?.uid, selectedTeam, checklistCache, getCacheKey]);
    
    // Reset fetch tracking when route params change
    useEffect(() => {
        hasInitialFetchRef.current = false;
        lastFetchParamsRef.current = '';
        lastFilterKeyRef.current = '';
    }, [sport, year, manufacturer, series, cardType, cardTypeCategory, targetUserID]);

    // Re-fetch card IDs when filters or metadata change (only after initial load)
    const lastFilterKeyRef = useRef('');
    useEffect(() => {
        // Skip if this is the initial load (handled by the first useEffect) or if custom category
        if (!hasInitialFetchRef.current || !targetUserID || isCustomCategory || !checklistMetadata?.sortOrder) {
            return;
        }
        
        const checklistPath = `checklists/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}`;
        const sortOrder = checklistMetadata.sortOrder;
        const filterKey = `${checklistPath}-${selectedTeam}-${sortOrder}`;
        
        // Skip if we've already fetched with these exact filter parameters
        if (lastFilterKeyRef.current === filterKey) {
            return;
        }
        
        lastFilterKeyRef.current = filterKey;
        
        // Reset to page 1 and fetch fresh card IDs when filters change
        setCurrentPage(1);
        // Clear existing checklist data to force fresh fetch
        dispatch(setChecklistData({}));
        dispatch(fetchChecklistCardIds(checklistPath, {
            team: selectedTeam,
            sortOrder: sortOrder
        }));
    }, [selectedTeam, checklistMetadata, targetUserID, dispatch, sport, year, manufacturer, series, cardType, cardTypeCategory, isCustomCategory]);

    // Store metadata and parallel data in cache when loaded (for official categories only)
    // Note: We don't cache checklistCardIds (team-filtered) or checklistData (on-demand fetched)
    useEffect(() => {
        if (isCustomCategory || !targetUserID) return;
        
        // Only cache metadata and parallel data (these are the same for all teams/users)
        const hasMetadata = checklistMetadata && checklistMetadata.sortOrder;
        
        if (hasMetadata) {
            const cacheKey = getCacheKey(sport, year, manufacturer, series, cardType, cardTypeCategory);
            
            // Store in cache (only if metadata or parallel data changed)
            const cachedData = checklistCache[cacheKey];
            const metadataChanged = !cachedData || 
                cachedData.metadata?.sortOrder !== checklistMetadata.sortOrder ||
                JSON.stringify(cachedData.metadata?.teams) !== JSON.stringify(checklistMetadata.teams);
            const parallelDataChanged = JSON.stringify(cachedData?.parallelData) !== JSON.stringify(parallelData);
            
            if (metadataChanged || parallelDataChanged) {
                dispatch(setChecklistCache(cacheKey, {
                    metadata: checklistMetadata || { sortOrder: 'numeric', teams: {} },
                    parallelData: parallelData || []
                }));
            }
        }
    }, [checklistMetadata, parallelData, sport, year, manufacturer, series, cardType, cardTypeCategory, isCustomCategory, targetUserID, dispatch, getCacheKey, checklistCache]);

    // Extract teams from metadata (optimized - uses pre-computed teams from _metadata)
    useEffect(() => {
        if (checklistMetadata?.teams && typeof checklistMetadata.teams === 'object') {
            // Teams are stored as an object with counts: { 'Dodgers': 45, 'Yankees': 32, ... }
            const teamNames = Object.keys(checklistMetadata.teams).sort();
            setTeams(['All Teams', ...teamNames]);
        } else {
            // Fallback: extract from checklistData if metadata doesn't have teams yet
            const allTeams = new Set();
            Object.values(checklistData).forEach(card => {
                if (card.team && Array.isArray(card.team)) {
                    card.team.forEach(team => allTeams.add(team));
                }
            });
            setTeams(['All Teams', ...Array.from(allTeams).sort()]);
        }
    }, [checklistMetadata, checklistData]);

    // Reset parallel filter when cardTypeCategory changes (different categories have different parallels)
    useEffect(() => {
        setSelectedParallel('All Parallels');
    }, [cardTypeCategory]);

    // Helper function to get numeric part for sorting
    const getNumericPart = useCallback((cardNumber) => {
        const parts = cardNumber.split('-');
        const lastPart = parts[parts.length - 1];
        return parseInt(lastPart.replace(/^\D+/g, ''), 10) || 0;
    }, []);

    // Sort card IDs (filtering by search term happens in fullyFilteredCardIds to support name search)
    const filteredAndSortedCardIds = useMemo(() => {
        // For custom categories, use cardData keys since all cards are collected
        if (isCustomCategory) {
            if (!effectiveCardData || Object.keys(effectiveCardData).length === 0) {
                return [];
            }
            const cardIds = Object.keys(effectiveCardData);
            // Sort IDs by numeric value
            return cardIds.sort((a, b) => {
                const aNum = getNumericPart(a);
                const bNum = getNumericPart(b);
                return aNum - bNum;
            });
        }

        // For official categories, use checklistCardIds
        if (!checklistCardIds || checklistCardIds.length === 0) {
            return [];
        }

        // Sort IDs by numeric value (no filtering here - let fullyFilteredCardIds handle search)
        const sortedIds = [...checklistCardIds].sort((a, b) => {
            const aNum = getNumericPart(a);
            const bNum = getNumericPart(b);
            return aNum - bNum;
        });

        return sortedIds;
    }, [checklistCardIds, effectiveCardData, isCustomCategory, getNumericPart]);

    // Further filter by name search (team filtering is already done in fetchChecklistCardIds)
    // checklistCardIds is already filtered by team when fetched, so we trust it
    const fullyFilteredCardIds = useMemo(() => {
        if (filteredAndSortedCardIds.length === 0) {
            return [];
        }

        // If no name search, we can use IDs directly (team filtering already done)
        if (enteredSearchTerm === '') {
            return filteredAndSortedCardIds;
        }

        // Only need to filter by name search
        // For cards we have data for, check name
        // For cards we don't have data for yet, include them if cardNumber matches (we'll fetch and filter later)
        const searchLower = enteredSearchTerm.toLowerCase();
        const idsWithData = filteredAndSortedCardIds.filter(cardId => {
            // OPTIMIZED: Use searchableCardNumber first (fastest check)
            const card = isCustomCategory ? effectiveCardData[cardId] : effectiveChecklistData[cardId];
            
            if (card?.searchableCardNumber?.includes(searchLower)) {
                return true;
            }

            // OPTIMIZED: Use searchableName if available, otherwise fall back to name array
            if (card?.searchableName?.includes(searchLower)) {
                return true;
            }

            // Fallback: Check name array for backward compatibility
            if (card && card.name && Array.isArray(card.name)) {
                return card.name.some(name => name.toLowerCase().includes(searchLower));
            }

            // If we don't have data yet, include it (might match when we fetch it)
            return true;
        });

        return idsWithData;
    }, [filteredAndSortedCardIds, enteredSearchTerm, effectiveChecklistData, effectiveCardData, isCustomCategory]);

    // Filter out collected cards when "Uncollected" stat is selected
    const uncollectedFilteredCardIds = useMemo(() => {
        if (selectedStat === "Uncollected" && isToggled) {
            // Filter out cards that are collected (exist in effectiveCardData with collectedCards)
            return fullyFilteredCardIds.filter(cardId => {
                const collectedCard = effectiveCardData[cardId];
                // Card is uncollected if it doesn't exist in cardData or has no collectedCards
                return !collectedCard || !collectedCard.collectedCards || collectedCard.collectedCards.length === 0;
            });
        }
        return fullyFilteredCardIds;
    }, [fullyFilteredCardIds, selectedStat, isToggled, effectiveCardData]);

    // Calculate total filtered count (from IDs, accurate)
    // For team filtering, we need to refine the count as data loads
    const totalFilteredCount = useMemo(() => {
        if (isToggled) {
            // Use uncollectedFilteredCardIds when "Uncollected" is selected, otherwise use fullyFilteredCardIds
            const idsToUse = selectedStat === "Uncollected" ? uncollectedFilteredCardIds : fullyFilteredCardIds;

            // For uncollected cards, use filtered IDs count
            // When team filter is active, we need to check loaded data to get accurate count
            if (selectedTeam !== 'All Teams') {
                // Count IDs that match team filter (we have data for) + IDs we haven't loaded yet
                const matchingIdsWithData = idsToUse.filter(cardId => {
                    // For custom categories, check cardData; for official categories, check checklistData
                    const card = isCustomCategory ? effectiveCardData[cardId] : effectiveChecklistData[cardId];
                    if (!card) return true; // Include unloaded cards (might match)
                    return card.team && Array.isArray(card.team) && card.team.includes(selectedTeam);
                });
                return matchingIdsWithData.length;
            }
            return idsToUse.length;
        } else {
            // For collected cards, use existing logic with effectiveCardData
            // For custom categories, this is the same as the uncollected view since all cards are collected
            if (!effectiveCardData || Object.keys(effectiveCardData).length === 0) return 0;
            const cards = Object.values(effectiveCardData);

            let preFiltered;
            if (enteredSearchTerm === '') {
                preFiltered = cards.filter(card => {
                    return selectedTeam === 'All Teams' || (card.team && card.team.includes(selectedTeam));
                });
            } else {
                const searchLower = enteredSearchTerm.toLowerCase();
                preFiltered = cards.filter(card => {
                    const matchesTeam = selectedTeam === 'All Teams' || (card.team && card.team.includes(selectedTeam));
                    // OPTIMIZED: Use searchable fields if available
                    const matchesSearch = card?.searchableCardNumber?.includes(searchLower) ||
                        card?.searchableName?.includes(searchLower) ||
                        // Fallback for backward compatibility
                        card.cardNumber.toLowerCase().includes(searchLower) ||
                        (card.name && card.name.some(name => name.toLowerCase().includes(searchLower)));
                    return matchesTeam && matchesSearch;
                });
            }

            // Apply stat-specific filters
            const statFiltered = preFiltered.filter(card => {
                const cardDataEntry = effectiveCardData[card.cardNumber];
                if (!cardDataEntry) {
                    return selectedStat === "Uncollected";
                }

                // OPTIMIZED: Use pre-computed flags if available, otherwise compute
                if (selectedStat === "TotalCards") {
                    return (cardDataEntry.totalQty !== undefined ? cardDataEntry.totalQty : 
                        cardDataEntry.collectedCards?.reduce((total, collectedCard) => total + collectedCard.cardQty, 0) || 0) > 0;
                } else if (selectedStat === "Completion") {
                    return true;
                } else if (selectedStat === "Uncollected") {
                    return !cardDataEntry.collectedCards || cardDataEntry.collectedCards.length === 0;
                } else if (selectedStat === "Base") {
                    return cardDataEntry.hasBase === true || 
                        (cardDataEntry.hasBase === undefined && cardDataEntry.collectedCards?.some(c => c.cardParallelName === "BASE"));
                } else if (selectedStat === "Parallels") {
                    return cardDataEntry.hasParallels === true || 
                        (cardDataEntry.hasParallels === undefined && cardDataEntry.collectedCards?.some(c => c.cardParallelName !== "BASE"));
                } else if (selectedStat === "Numbered") {
                    return cardDataEntry.hasNumbered === true || 
                        (cardDataEntry.hasNumbered === undefined && cardDataEntry.collectedCards?.some(c => c.cardPrintNumber));
                } else if (selectedStat === "Duplicates") {
                    return cardDataEntry.hasDuplicates === true || 
                        (cardDataEntry.hasDuplicates === undefined && cardDataEntry.collectedCards?.some(c => c.cardQty > 1));
                }
                return true;
            });

            // Apply parallel filter if selected
            const parallelFiltered = selectedParallel !== 'All Parallels'
                ? statFiltered.filter(card => {
                    const cardDataEntry = effectiveCardData[card.cardNumber];
                    // OPTIMIZED: Use parallelNames array if available, otherwise fall back
                    if (cardDataEntry?.parallelNames) {
                        return cardDataEntry.parallelNames.includes(selectedParallel);
                    }
                    return cardDataEntry?.collectedCards?.some(c => c.cardParallelName === selectedParallel);
                })
                : statFiltered;

            return parallelFiltered.length;
        }
    }, [isToggled, selectedTeam, selectedStat, selectedParallel, enteredSearchTerm, effectiveCardData, fullyFilteredCardIds, uncollectedFilteredCardIds, effectiveChecklistData, isCustomCategory]);

    // Step 2: Get IDs for current page
    const paginatedCardIds = useMemo(() => {
        if (isToggled) {
            // Use uncollectedFilteredCardIds when "Uncollected" is selected, otherwise use fullyFilteredCardIds
            const idsToUse = selectedStat === "Uncollected" ? uncollectedFilteredCardIds : fullyFilteredCardIds;
            const startIndex = (currentPage - 1) * cardsPerPage;
            const endIndex = startIndex + cardsPerPage;
            return idsToUse.slice(startIndex, endIndex);
        } else {
            // For collected cards, use effectiveCardData keys filtered by the current filters
            if (!effectiveCardData || Object.keys(effectiveCardData).length === 0) return [];

            // Get all collected card numbers
            let collectedIds = Object.keys(effectiveCardData);

            // Filter by search term if provided
            if (enteredSearchTerm) {
                const searchLower = enteredSearchTerm.toLowerCase();
                collectedIds = collectedIds.filter(cardId => {
                    const card = effectiveCardData[cardId];
                    // OPTIMIZED: Use searchable fields if available
                    if (card?.searchableCardNumber?.includes(searchLower)) {
                        return true;
                    }
                    if (card?.searchableName?.includes(searchLower)) {
                        return true;
                    }
                    // Fallback for backward compatibility
                    if (card && card.name && Array.isArray(card.name)) {
                        return card.name.some(name => name.toLowerCase().includes(searchLower));
                    }
                    return cardId.toLowerCase().includes(searchLower);
                });
            }

            // Filter by team if selected
            if (selectedTeam !== 'All Teams') {
                collectedIds = collectedIds.filter(cardId => {
                    const card = effectiveCardData[cardId];
                    // For custom categories, check cardData; for official categories, check checklistData
                    const sourceCard = isCustomCategory ? card : effectiveChecklistData[cardId];
                    return sourceCard && sourceCard.team && Array.isArray(sourceCard.team) && sourceCard.team.includes(selectedTeam);
                });
            }

            // Apply stat-specific filters
            collectedIds = collectedIds.filter(cardId => {
                const card = effectiveCardData[cardId];
                if (!card || !card.collectedCards || card.collectedCards.length === 0) return false;

                // OPTIMIZED: Use pre-computed flags if available, otherwise compute
                if (selectedStat === "TotalCards") {
                    return (card.totalQty !== undefined ? card.totalQty : 
                        card.collectedCards.reduce((total, collectedCard) => total + collectedCard.cardQty, 0)) > 0;
                } else if (selectedStat === "Base") {
                    return card.hasBase === true || 
                        (card.hasBase === undefined && card.collectedCards.some(c => c.cardParallelName === "BASE"));
                } else if (selectedStat === "Parallels") {
                    return card.hasParallels === true || 
                        (card.hasParallels === undefined && card.collectedCards.some(c => c.cardParallelName !== "BASE"));
                } else if (selectedStat === "Numbered") {
                    return card.hasNumbered === true || 
                        (card.hasNumbered === undefined && card.collectedCards.some(c => c.cardPrintNumber));
                } else if (selectedStat === "Duplicates") {
                    return card.hasDuplicates === true || 
                        (card.hasDuplicates === undefined && card.collectedCards.some(c => c.cardQty > 1));
                }
                return true; // Completion and other stats show all collected cards
            });

            // Apply parallel filter if selected
            if (selectedParallel !== 'All Parallels') {
                collectedIds = collectedIds.filter(cardId => {
                    const card = effectiveCardData[cardId];
                    // OPTIMIZED: Use parallelNames array if available, otherwise fall back
                    if (card?.parallelNames) {
                        return card.parallelNames.includes(selectedParallel);
                    }
                    return card?.collectedCards?.some(c => c.cardParallelName === selectedParallel);
                });
            }

            // Sort by card number (numeric)
            collectedIds.sort((a, b) => {
                const aNum = getNumericPart(a);
                const bNum = getNumericPart(b);
                if (aNum !== bNum) return aNum - bNum;
                return a.localeCompare(b);
            });

            // Paginate
            const startIndex = (currentPage - 1) * cardsPerPage;
            const endIndex = startIndex + cardsPerPage;
            return collectedIds.slice(startIndex, endIndex);
        }
    }, [isToggled, fullyFilteredCardIds, uncollectedFilteredCardIds, selectedStat, selectedParallel, currentPage, cardsPerPage, effectiveCardData, enteredSearchTerm, selectedTeam, isCustomCategory, effectiveChecklistData, getNumericPart]);

    // Step 3: Fetch full data for IDs we need but don't have (for both collected and uncollected views)
    const lastFetchedIdsRef = useRef(new Set());
    useEffect(() => {
        if (targetUserID && paginatedCardIds.length > 0 && !isCustomCategory) {
            const checklistPath = `checklists/${sport}/${year}/${manufacturer}/${series}/${cardType}/${cardTypeCategory}`;

            // Find IDs we need but don't have data for
            const idsToFetch = paginatedCardIds.filter(cardId => !checklistData[cardId] && !lastFetchedIdsRef.current.has(cardId));

            if (idsToFetch.length > 0) {
                // Track which IDs we're fetching to prevent duplicate requests
                idsToFetch.forEach(id => lastFetchedIdsRef.current.add(id));
                dispatch(fetchChecklistDataByIds(checklistPath, idsToFetch));
            }
        }
        // For custom categories, all data comes from collection, no need to fetch checklist data
    }, [paginatedCardIds, checklistData, targetUserID, dispatch, sport, year, manufacturer, series, cardType, cardTypeCategory, isCustomCategory]);
    
    // Clear fetched IDs cache when route params change
    useEffect(() => {
        lastFetchedIdsRef.current.clear();
    }, [sport, year, manufacturer, series, cardType, cardTypeCategory]);

    // Cache for stable card object references - only updates when actual card data changes
    const cardObjectCache = useRef(new Map());
    const previousCardDataRef = useRef(cardData);

    // Track when cardData actually changes (not just queuedCardsDisplay)
    useEffect(() => {
        if (previousCardDataRef.current !== cardData) {
            // Card data changed - clear cache for affected cards
            previousCardDataRef.current = cardData;
            // Clear cache when cardData reference changes (actual data update)
            cardObjectCache.current.clear();
        }
    }, [cardData]);

    // Step 4: Create card objects for display with stable references
    const filteredCards = useMemo(() => {
        const cache = cardObjectCache.current;

        // Helper to get or create stable card object
        const getStableCardObject = (cardNumber, cardObj) => {
            // Use cardNumber as cache key - if card data hasn't changed, return cached version
            if (cache.has(cardNumber)) {
                const cached = cache.get(cardNumber);
                // Verify the cached card's collectedCards reference matches the source data
                const sourceCard = effectiveCardData[cardNumber];
                if (sourceCard && cached.collectedCards === sourceCard.collectedCards) {
                    return cached;
                }
                // If no source card or reference changed, we need to update cache
            }
            // Create new object and cache it
            const stableCard = { ...cardObj };
            // Preserve reference to original collectedCards if available
            const sourceCard = effectiveCardData[cardNumber];
            if (sourceCard && sourceCard.collectedCards) {
                stableCard.collectedCards = sourceCard.collectedCards;
            }
            cache.set(cardNumber, stableCard);
            return stableCard;
        };

        if (isToggled) {
            // When "Uncollected" is selected, filter out any collected cards that might have slipped through
            return paginatedCardIds
                .filter(cardNumber => {
                    // If "Uncollected" is selected, exclude cards that are collected
                    if (selectedStat === "Uncollected") {
                        const collectedCard = effectiveCardData[cardNumber];
                        // Only include if card is not collected
                        return !collectedCard || !collectedCard.collectedCards || collectedCard.collectedCards.length === 0;
                    }
                    return true; // Include all cards for other stats
                })
                .map(cardNumber => {
                    // For custom categories, use cardData as the source (since there's no checklist)
                    if (isCustomCategory) {
                        const collectedCard = effectiveCardData[cardNumber];
                        if (collectedCard) {
                            const cardObj = { ...collectedCard, isCollected: true };
                            return getStableCardObject(cardNumber, cardObj);
                        } else {
                            // Card not loaded yet - return new object (no need to cache placeholders)
                            return {
                                cardNumber,
                                name: [],
                                team: [],
                                collectedCards: [],
                                isCollected: false,
                                opacity: 0.5,
                                isLoading: true
                            };
                        }
                    }

                    // For official categories, use existing logic
                    const checklistCard = effectiveChecklistData[cardNumber];
                    if (!checklistCard) {
                        // Card data not loaded yet - return new object (no need to cache placeholders)
                        return {
                            cardNumber,
                            name: [],
                            team: [],
                            collectedCards: [],
                            isCollected: false,
                            opacity: 0.5,
                            isLoading: true
                        };
                    }
                    const collectedCard = effectiveCardData[cardNumber];
                    const cardObj = collectedCard
                        ? { ...collectedCard, isCollected: true }
                        : { ...checklistCard, collectedCards: [], isCollected: false, opacity: 0.5 };
                    return getStableCardObject(cardNumber, cardObj);
                });
        } else {
            // For collected cards view (isToggled = false)
            // Use paginatedCardIds which now contains the filtered collected card IDs
            if (!paginatedCardIds || paginatedCardIds.length === 0) return [];

            return paginatedCardIds.map(cardNumber => {
                const collectedCard = effectiveCardData[cardNumber];
                if (!collectedCard) return null;

                // For custom categories, use cardData as the source
                if (isCustomCategory) {
                    const cardObj = { ...collectedCard, isCollected: true };
                    return getStableCardObject(cardNumber, cardObj);
                }

                // For official categories, merge with checklist data if available
                const checklistCard = effectiveChecklistData[cardNumber];
                const cardObj = checklistCard
                    ? { ...checklistCard, ...collectedCard, isCollected: true }
                    : { ...collectedCard, isCollected: true };
                return getStableCardObject(cardNumber, cardObj);
            })
                .filter(card => card !== null);
        }
    }, [isToggled, paginatedCardIds, effectiveChecklistData, effectiveCardData, isCustomCategory, selectedStat]);

    // Calculate total pages based on filtered results
    const totalPages = useMemo(() => {
        return Math.ceil(totalFilteredCount / cardsPerPage);
    }, [totalFilteredCount, cardsPerPage]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedTeam, selectedStat, enteredSearchTerm, isToggled]);

    // Note: Data fetching for needed IDs is now handled in the useEffect above
    // that watches paginatedCardIds

    // Handler for card selection
    const handleCardSelectionChange = useCallback((cardNumber, isSelected) => {
        setSelectedCards(prev => {
            const newSet = new Set(prev);
            if (isSelected) {
                newSet.add(cardNumber);
            } else {
                newSet.delete(cardNumber);
            }
            return newSet;
        });
    }, []);

    // Optimized card component with proper memoization for large datasets
    // When viewing a friend, use FriendsChecklistDisplayItem (customizable); otherwise ChecklistItemDisplay
    const CardComponent = memo(({ card, index, viewingUserID, parallelData, isSelected, onSelectionChange }) => {
        const parallels = parallelData || [];
        const cardOpacity = card.isCollected ? 1 : (card.opacity || 1);

        const itemProps = {
            card,
            parallels,
            cardOpacity,
            selectedParallelFilter: selectedParallel,
            isSelected,
            onSelectionChange
        };

        const ItemDisplay = viewingUserID ? FriendsChecklistDisplayItem : ChecklistItemDisplay;

        return (
            <div style={{ alignSelf: 'start' }}>
                <ItemDisplay {...itemProps} />
            </div>
        );
    }, (prevProps, nextProps) => {
        const prevIsSelected = prevProps.isSelected;
        const nextIsSelected = nextProps.isSelected;
        const shouldSkipRender = (
            prevProps.card.cardNumber === nextProps.card.cardNumber &&
            prevProps.viewingUserID === nextProps.viewingUserID &&
            prevProps.card === nextProps.card &&
            prevProps.parallelData === nextProps.parallelData &&
            prevIsSelected === nextIsSelected
        );

        return shouldSkipRender;
    });

    const handleStatBoxFilterClick = (statType) => {
        switch (statType) {
            case "TotalCards":
                setIsToggled(false);
                setSelectedStat("TotalCards");
                break;
            case "Completion":
                setIsToggled(true);
                setSelectedStat("Completion");
                break;
            case "Uncollected":
                setIsToggled(true);
                setSelectedStat("Uncollected");
                break;
            case "Base":
                setIsToggled(false);
                setSelectedStat("Base");
                break;
            case "Parallels":
                setIsToggled(false);
                setSelectedStat("Parallels");
                break;
            case "Numbered":
                setIsToggled(false);
                setSelectedStat("Numbered");
                break;
            case "Duplicates":
                setIsToggled(false);
                setSelectedStat("Duplicates");
                break;
            default:
                setIsToggled(true);
                setSelectedStat(null);
                break;
        }
    }

    // Removed handleToggleClick function - toggle removed

    const fadeInFromRight = {
        hidden: {
            opacity: 0,
            x: 100, // Start 100px to the right
        },
        visible: {
            opacity: 1,
            x: 0, // Animate to its original position
            transition: {
                duration: 0.2,
                ease: "easeOut"
            }
        }
    };

    return (
        <>
            {isLoading ? (
                <div style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1000
                }}>
                    <LoadingIcon />
                </div>
            ) : (
                <SeriesDisplayContainer>

                    {/* Insights */}
                    <DisplayHeader className='panel-bg dropshadow'>
                        <h3>{selectedTeam} Collection Insights</h3>
                        <CollectionStats>
                            <div onClick={() => handleStatBoxFilterClick("TotalCards")}>
                                <StatBox
                                    isActive={selectedStat === "TotalCards"}
                                    label={"Collected Cards"}
                                    value={teamFilteredStats.totalCollectedTeamCards}
                                />
                            </div>
                            <div onClick={() => handleStatBoxFilterClick("Completion")}>
                                <StatBox
                                    isActive={selectedStat === "Completion"}
                                    label={"Completion"}
                                    value={`${teamFilteredStats.collectedTeamCards} of ${teamFilteredStats.totalTeamCards} (${completionPercentage}%)`}
                                />
                            </div>
                            <div onClick={() => handleStatBoxFilterClick("Uncollected")}>
                                <StatBox
                                    isActive={selectedStat === "Uncollected"}
                                    label={"Uncollected"}
                                    value={teamFilteredStats.uncollectedTeamCards}
                                />
                            </div>
                            <div onClick={() => handleStatBoxFilterClick("Base")}>
                                <StatBox
                                    isActive={selectedStat === "Base"}
                                    label={"Base"}
                                    value={teamFilteredStats.baseCardsCount}
                                />
                            </div>
                            <div onClick={() => handleStatBoxFilterClick("Parallels")}>
                                <StatBox
                                    isActive={selectedStat === "Parallels"}
                                    label={"Parallels"}
                                    value={teamFilteredStats.parallelCardsCount}
                                />
                            </div>
                            <div onClick={() => handleStatBoxFilterClick("Numbered")}>
                                <StatBox
                                    isActive={selectedStat === "Numbered"}
                                    label={"Numbered"}
                                    value={teamFilteredStats.numberedCardsCount}
                                />
                            </div>
                            <div onClick={() => handleStatBoxFilterClick("Duplicates")}>
                                <StatBox
                                    isActive={selectedStat === "Duplicates"}
                                    label={"Duplicates"}
                                    value={teamFilteredStats.duplicatesCount}
                                />
                            </div>
                        </CollectionStats>
                    </DisplayHeader>

                    {/* Body */}

                    <BodyContainer>
                        <LeftContainer>
                            {/* Add Card Button for Custom Categories - only show for own collection */}
                            {isCustomCategory && !viewingUserID && (
                                <div style={{ marginBottom: '16px' }}>
                                    <PrimaryBtn
                                        title="+ ADD CARD"
                                        onClick={() => setIsAddCardModalOpen(true)}
                                    />
                                </div>
                            )}
                            {/* Search Bar & Filters */}
                            <ChecklistControls>
                                <ControlContent>
                                    <InputField
                                        type='text'
                                        placeholder='SEARCH...'
                                        value={searchInputValue}
                                        onChange={e => setSearchInputValue(e.target.value)}
                                    />
                                </ControlContent>
                                <LeftFilterContainer>


                                    {parallelData && parallelData.length > 0 &&
                                        <ControlContent>
                                            <select
                                                id="parallelFilter"
                                                value={selectedParallel}
                                                onChange={(e) => setSelectedParallel(e.target.value)}
                                                className='panel-bg'
                                                style={{ minWidth: '150px' }}
                                            >
                                                <option value="All Parallels">All Parallels</option>
                                                <option value="BASE">BASE</option>
                                                {parallelData.map((parallel, index) => (
                                                    <option key={index} value={parallel.name}>
                                                        {parallel.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </ControlContent>
                                    }
                                    {((teamFilteredStats.collectedTeamCards !== 0) || (isToggled)) &&
                                        <ControlContent>
                                            <select
                                                id="teamFilter"
                                                value={selectedTeam}
                                                onChange={(e) => setSelectedTeam(e.target.value)}
                                                className='panel-bg'
                                            >
                                                {teams.map((team, index) => (
                                                    <option key={index} value={team}>
                                                        {team}
                                                    </option>
                                                ))}
                                            </select>
                                        </ControlContent>
                                    }
                                </LeftFilterContainer>
                            </ChecklistControls>

                            {/* Cards Display */}
                            <CardsDisplayContainer>

                                {/* Cards */}
                                <CardsDisplay>
                                    {filteredCards.length > 0 &&
                                        filteredCards.map((card, index) => (
                                            <CardComponent
                                                key={card.cardNumber}
                                                card={card}
                                                index={index}
                                                viewingUserID={viewingUserID}
                                                parallelData={parallelData}
                                                isSelected={selectedCards.has(card.cardNumber)}
                                                onSelectionChange={handleCardSelectionChange}
                                            />
                                        ))}
                                </CardsDisplay>

                                {/* Pagination Controls */}
                                {filteredCards.length > 0 && (
                                    <SystemPaginationControls
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        isLoading={isLoading}
                                        onPageChange={setCurrentPage}
                                    />
                                )}

                            </CardsDisplayContainer>
                        </LeftContainer>

                        {/* Queued Cards - only show for own collection */}
                        {queuedCardsDisplay.length !== 0 && !viewingUserID &&
                            <QueuedCardsContainer
                                $fixed={isFixed}
                                initial="hidden"
                                animate="visible"
                                variants={fadeInFromRight}
                            >
                                <QueuedCards />
                            </QueuedCardsContainer>
                        }
                    </BodyContainer>
                </SeriesDisplayContainer>
            )}
            {!viewingUserID && (
                <AddCustomCardModal
                    isOpen={isAddCardModalOpen}
                    onClose={() => setIsAddCardModalOpen(false)}
                    sport={sport}
                    year={year}
                    manufacturer={manufacturer}
                    series={series}
                    cardType={cardType}
                    cardTypeCategory={cardTypeCategory}
                />
            )}
            {/* Batch Modals */}
            {showBatchSetModal && (
                <BatchSetSelectionModal
                    isOpen={showBatchSetModal}
                    onClose={() => {
                        setShowBatchSetModal(false);
                        setSelectedCards(new Set());
                    }}
                    cardNumbers={Array.from(selectedCards)}
                    filteredCards={filteredCards}
                    onSuccess={() => {
                        setShowBatchSetModal(false);
                        setSelectedCards(new Set());
                    }}
                />
            )}
            {showBatchListingModal && (
                <BatchListingSelectionModal
                    isOpen={showBatchListingModal}
                    onClose={() => {
                        setShowBatchListingModal(false);
                        setSelectedCards(new Set());
                    }}
                    cardNumbers={Array.from(selectedCards)}
                    filteredCards={filteredCards}
                    onSuccess={() => {
                        setShowBatchListingModal(false);
                        setSelectedCards(new Set());
                    }}
                />
            )}
            {/* Fixed Action Buttons */}
            {selectedCards.size > 0 && !viewingUserID && (
                <FixedActionButtonsContainer>
                    <PrimaryBtn
                        title={`ADD TO SET (${selectedCards.size})`}
                        onClick={() => setShowBatchSetModal(true)}
                    />
                    <PrimaryBtn
                        title={`ADD TO SALE (${selectedCards.size})`}
                        onClick={() => setShowBatchListingModal(true)}
                    />
                    <PrimaryBtn
                        title="DESELECT ALL"
                        onClick={() => setSelectedCards(new Set())}
                    />
                </FixedActionButtonsContainer>
            )}
        </>
    );
}